import { trackBookingStepEvent } from "../../../AnalyticsTracking/app-insights-telemetry";
import { emitEvent } from "../../../AnalyticsTracking/emit-event";
import { EVENT_TYPE } from "../../../AnalyticsTracking/events";
import { CareUnit, ContactDetailsSubStep, ProvetDialogStep, Reason } from "../types";

interface TrackingEvent extends Record<string, unknown> {
  category: string;
  label: string;
  action: string;
  bookingStepNumber: number | string;
  bookingStepName: string;
  bookingStepComplete: boolean;
  bookingSessionId: string;
  bookingId: number | null;
  clinicName: string | null;
  clinicPmsId: string | null;
  clinicBId: string | null;
  clinicCmsId: string | null;
  petSpecies: string | null;
  treatmentPmsId: string | null;
  treatmentName: string | null;
  userEmail: string | null;
}

interface TrackingServiceOptions {
  advancedTrackingEnabled: boolean;
  sessionId: string;
  selectedCareUnit?: CareUnit;
  selectedReason?: Reason;
  petSpeciesName?: string;
  hashedEmail?: string;
  bookingId?: number;
}

/**
 * Configuration for each dialog step's tracking behavior.
 * This configuration map follows the Open/Closed Principle - new steps
 * can be added by extending this configuration without modifying the class logic.
 */
interface StepTrackingConfig {
  /** The numeric step number for tracking */
  stepNumber: number;
  /** Whether this step is tracked in basic tracking mode */
  basicTracking: boolean;
  /** Whether this step is tracked in advanced tracking mode */
  advancedTracking: boolean;
}

/**
 * Step tracking configuration map.
 * To add a new step, simply add a new entry here without modifying any methods.
 */
const STEP_TRACKING_CONFIG: Record<ProvetDialogStep, StepTrackingConfig> = {
  [ProvetDialogStep.ImportantInformation]: {
    stepNumber: 0,
    basicTracking: false,
    advancedTracking: false
  },
  [ProvetDialogStep.SelectClinic]: {
    stepNumber: 1,
    basicTracking: true,
    advancedTracking: true
  },
  [ProvetDialogStep.SelectTreatment]: {
    stepNumber: 2,
    basicTracking: false,
    advancedTracking: true
  },
  [ProvetDialogStep.SelectDateAndTime]: {
    stepNumber: 3,
    basicTracking: false,
    advancedTracking: true
  },
  [ProvetDialogStep.ContactDetails]: {
    stepNumber: 4,
    basicTracking: false,
    advancedTracking: true
  },
  [ProvetDialogStep.Confirmation]: {
    stepNumber: 5,
    basicTracking: false,
    advancedTracking: true
  },
  [ProvetDialogStep.Confirmed]: {
    stepNumber: 6,
    basicTracking: true,
    advancedTracking: true
  }
};

/**
 * The sub-steps of the ContactDetails dialog step are tracked separately.
 */
interface SubStepTrackingConfig {
  /** The `bookingStepNumber` of the event, also used in its label */
  bookingStepNumber: number | string;
  /** The `bookingStepName` (and action) of the event */
  stepName: string;
}

const CONTACT_DETAILS_SUB_STEP_CONFIG: Record<
  ContactDetailsSubStep,
  SubStepTrackingConfig
> = {
  // The numbers sort between step 3 (date and time) and step 4 (the last sub-step)
  emailAndPhone: {
    bookingStepNumber: "3a",
    stepName: "SmsVerificationContactDetails"
  },
  verifySms: {
    bookingStepNumber: "3b",
    stepName: "SmsVerification"
  },
  // The last sub-step is intentionally identical to the ContactDetails step, so that
  // historic data isn't split.
  details: {
    bookingStepNumber: STEP_TRACKING_CONFIG[ProvetDialogStep.ContactDetails].stepNumber,
    stepName: ProvetDialogStep[ProvetDialogStep.ContactDetails]
  }
};

/**
 * Service for tracking Provet online booking events to analytics.
 *
 * Supports two tracking modes:
 * - Basic: Tracks only start (ImportantInformation/SelectClinic) and completion (Confirmed)
 * - Advanced: Tracks all steps in the booking flow
 *
 * The service uses a configuration-based approach (STEP_TRACKING_CONFIG) to determine
 * which steps are tracked, following the Open/Closed Principle. New steps can be added
 * by extending the configuration without modifying the service logic.
 */
export class TrackingService {
  private options: TrackingServiceOptions;
  private readonly TRACKING_EVENT_NAME = "provet_online_booking";

  constructor(options: TrackingServiceOptions) {
    this.options = options;
  }

  /**
   * Updates the tracking service options.
   * @param updates - Partial updates to apply to current options
   */
  updateOptions(updates: Partial<TrackingServiceOptions>): void {
    this.options = { ...this.options, ...updates };
  }

  /**
   * Tracks a booking event for the specified dialog step.
   *
   * @param provetDialogStep - The current step in the booking flow
   * @param isBookingStepComplete - Whether the step has been completed (default: false)
   * @param contactDetailsSubStep - Which sub-step of ContactDetails the event is for.
   * Only used for the ContactDetails step, and defaults to the last sub-step.
   */
  async trackBookingEvent(
    provetDialogStep: ProvetDialogStep,
    isBookingStepComplete: boolean = false,
    contactDetailsSubStep: ContactDetailsSubStep = "details"
  ): Promise<void> {
    // Application Insights gets every step transition (operational visibility), independent of
    // the analytics (GTM/Jentis) tracking mode below.
    this.trackStepInAppInsights(provetDialogStep, isBookingStepComplete);

    const shouldTrackEvent = this.shouldTrackEvent(
      provetDialogStep,
      isBookingStepComplete
    );

    if (!shouldTrackEvent) {
      return;
    }

    const config = this.getStepConfig(provetDialogStep);
    const bookingStepComplete =
      provetDialogStep === ProvetDialogStep.Confirmed || isBookingStepComplete;
    const subStepConfig =
      provetDialogStep === ProvetDialogStep.ContactDetails
        ? CONTACT_DETAILS_SUB_STEP_CONFIG[contactDetailsSubStep]
        : undefined;
    const bookingStepNumber = subStepConfig?.bookingStepNumber ?? config.stepNumber;
    const bookingStepName = subStepConfig?.stepName ?? ProvetDialogStep[provetDialogStep];
    const category = this.TRACKING_EVENT_NAME;
    const label = `Step ${bookingStepNumber} - ${bookingStepName}`;
    const action = bookingStepName;

    const trackingEvent: TrackingEvent = {
      category,
      label,
      action,
      bookingStepNumber,
      bookingStepName,
      bookingStepComplete,
      bookingSessionId: this.options.sessionId,
      bookingId: this.options.bookingId ?? null,
      clinicName: this.options.selectedCareUnit?.name ?? null,
      clinicPmsId: this.options.selectedCareUnit?.pmsId ?? null,
      clinicBId: this.options.selectedCareUnit?.businessId ?? null,
      clinicCmsId: this.options.selectedCareUnit?.id?.toString() ?? null,
      petSpecies: this.options.petSpeciesName || null,
      treatmentPmsId: this.options.selectedReason?.id?.toString() ?? null,
      treatmentName: this.options.selectedReason?.name ?? null,
      userEmail: this.options.hashedEmail || null
    };

    try {
      await emitEvent(EVENT_TYPE.onlineBooking, trackingEvent, true);
    } catch (error) {
      console.error("Error tracking booking event:", error);
    }
  }

  /**
   * Sends a BookingStep custom event to Application Insights. Contains no personal data.
   */
  private trackStepInAppInsights(
    provetDialogStep: ProvetDialogStep,
    isBookingStepComplete: boolean
  ): void {
    const config = this.getStepConfig(provetDialogStep);

    trackBookingStepEvent({
      flow: "Provet",
      step: ProvetDialogStep[provetDialogStep],
      stepNumber: config?.stepNumber,
      complete: provetDialogStep === ProvetDialogStep.Confirmed || isBookingStepComplete,
      bookingSessionId: this.options.sessionId,
      clinicId: this.options.selectedCareUnit?.id,
      clinicPmsId: this.options.selectedCareUnit?.pmsId
    });
  }

  /**
   * Retrieves the tracking configuration for a given step.
   * @param step - The dialog step
   * @returns The step's tracking configuration
   */
  private getStepConfig(step: ProvetDialogStep): StepTrackingConfig {
    return STEP_TRACKING_CONFIG[step];
  }

  /**
   * Determines whether an event should be tracked based on step configuration and tracking mode.
   *
   * @param provetDialogStep - The current dialog step
   * @param isBookingStepComplete - Whether the step is complete
   * @returns True if the event should be tracked
   */
  private shouldTrackEvent(
    provetDialogStep: ProvetDialogStep,
    isBookingStepComplete: boolean
  ): boolean {
    const config = this.getStepConfig(provetDialogStep);

    // Important Information step is never tracked
    if (provetDialogStep === ProvetDialogStep.ImportantInformation) {
      return false;
    }

    // Confirmed step is always tracked
    if (provetDialogStep === ProvetDialogStep.Confirmed) {
      return true;
    }

    // For steps with basic tracking, only track when complete
    if (config.basicTracking && !this.options.advancedTrackingEnabled) {
      return isBookingStepComplete;
    }

    // For advanced tracking mode, track all steps that support it
    if (this.options.advancedTrackingEnabled && config.advancedTracking) {
      return true;
    }

    return false;
  }
}
